import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, MessageCircle, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import {
  CONTACT,
  FUNDING_RANGES,
  FUNDING_PURPOSES,
  CONTACT_METHODS,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  company: "",
  designation: "",
  phone: "",
  email: "",
  funding_requirement: "",
  purpose: "",
  description: "",
  preferred_contact: "",
};

const inputCls =
  "w-full border border-white/10 bg-[#0B0F17] px-4 py-3.5 text-sm font-light text-slate-200 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-[#F26122]/60";

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      axios.post(`${API}/enquiries`, form).catch(() => {});
      await axios.post(
        "https://formsubmit.co/ajax/jeetadvisoryservices@gmail.com",
        {
          _subject: `New capital requirement — ${form.name}`,
          _template: "table",
          _replyto: form.email,
          ...form,
        }
      );
      setSubmitted(true);
      toast.success("Requirement received", {
        description: "Our team will review your requirement and reach out shortly.",
      });
    } catch (err) {
      toast.error("Submission failed", {
        description: "Please try again, or reach us directly on WhatsApp or email.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-white/5 bg-[#080C13]"
    >
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          <div>
            <SectionHeading
              number="09"
              label="Contact"
              title="Have a capital requirement?"
              tone="dark"
            />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-md font-display text-2xl font-light italic text-slate-300">
                Let&rsquo;s understand the requirement first.
              </p>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-slate-400">
                Tell us about your funding requirement and our team will
                evaluate the appropriate capital route.
              </p>
            </Reveal>

            <div className="mt-12 space-y-px border border-white/10 bg-white/10">
              <Reveal delay={0.15}>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${CONTACT.email}`}
                  className="group flex items-center justify-between bg-[#080C13] px-6 py-5 transition-colors duration-300 hover:bg-[#111827]"
                >
                  <span className="flex items-center gap-4">
                    <Mail className="h-4 w-4 text-[#F2824F]" />
                    <span>
                      <span className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        Email
                      </span>
                      <span className="mt-1 block text-sm text-slate-200">
                        {CONTACT.email}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:text-[#F26122]" />
                </a>
              </Reveal>
              <Reveal delay={0.2}>
                <a
                  data-testid="contact-whatsapp-link"
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between bg-[#080C13] px-6 py-5 transition-colors duration-300 hover:bg-[#111827]"
                >
                  <span className="flex items-center gap-4">
                    <MessageCircle className="h-4 w-4 text-[#F2824F]" />
                    <span>
                      <span className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        WhatsApp
                      </span>
                      <span className="mt-1 block text-sm text-slate-200">
                        {CONTACT.phoneDisplay}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:text-[#F26122]" />
                </a>
              </Reveal>
              <Reveal delay={0.25}>
                <a
                  data-testid="contact-linkedin-link"
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between bg-[#080C13] px-6 py-5 transition-colors duration-300 hover:bg-[#111827]"
                >
                  <span className="flex items-center gap-4">
                    <Linkedin className="h-4 w-4 text-[#F2824F]" />
                    <span>
                      <span className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        LinkedIn
                      </span>
                      <span className="mt-1 block text-sm text-slate-200">
                        {CONTACT.linkedinName}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:text-[#F26122]" />
                </a>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="relative border border-white/10 bg-[#0D1220] p-8 sm:p-10">
              <div className="absolute -left-4 -top-4 h-16 w-16 border-b border-l border-[#F26122]/40" aria-hidden="true" />

              {submitted ? (
                <div
                  data-testid="contact-form-success"
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                >
                  <CheckCircle2 className="h-10 w-10 text-[#F26122]" />
                  <h3 className="mt-6 font-display text-2xl font-light text-slate-100">
                    Requirement received.
                  </h3>
                  <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-slate-400">
                    Thank you for reaching out. Our team will evaluate your
                    requirement and contact you through your preferred channel.
                  </p>
                  <button
                    data-testid="contact-form-reset-button"
                    onClick={() => {
                      setForm(initialForm);
                      setSubmitted(false);
                    }}
                    className="mt-8 border border-white/15 px-6 py-3 text-xs tracking-wide text-slate-300 transition-colors duration-300 hover:border-[#F26122]/60 hover:text-[#F26122]"
                  >
                    Submit another requirement
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">
                    Start a conversation
                  </p>

                  <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <input
                      data-testid="form-field-name"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Name *"
                      className={inputCls}
                    />
                    <input
                      data-testid="form-field-company"
                      value={form.company}
                      onChange={set("company")}
                      placeholder="Company"
                      className={inputCls}
                    />
                    <input
                      data-testid="form-field-designation"
                      value={form.designation}
                      onChange={set("designation")}
                      placeholder="Designation"
                      className={inputCls}
                    />
                    <input
                      data-testid="form-field-phone"
                      required
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="Phone *"
                      className={inputCls}
                    />
                    <input
                      data-testid="form-field-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="Email *"
                      className={inputCls}
                    />
                    <select
                      data-testid="form-field-funding-requirement"
                      value={form.funding_requirement}
                      onChange={set("funding_requirement")}
                      className={inputCls}
                    >
                      <option value="">Approximate Funding Requirement</option>
                      {FUNDING_RANGES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <select
                      data-testid="form-field-purpose"
                      value={form.purpose}
                      onChange={set("purpose")}
                      className={inputCls}
                    >
                      <option value="">Purpose of Funding</option>
                      {FUNDING_PURPOSES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    <select
                      data-testid="form-field-preferred-contact"
                      value={form.preferred_contact}
                      onChange={set("preferred_contact")}
                      className={inputCls}
                    >
                      <option value="">Preferred Contact Method</option>
                      {CONTACT_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <textarea
                      data-testid="form-field-description"
                      value={form.description}
                      onChange={set("description")}
                      placeholder="Brief description of the requirement"
                      rows={4}
                      className={`${inputCls} resize-none sm:col-span-2`}
                    />
                  </div>

                  <p
                    data-testid="form-confidentiality-note"
                    className="mt-5 text-xs font-light italic leading-relaxed text-slate-500"
                  >
                    Please do not submit confidential documents or sensitive
                    financial information through this form. Our team will
                    advise you on the appropriate next step.
                  </p>

                  <button
                    data-testid="contact-form-submit"
                    type="submit"
                    disabled={submitting}
                    className="group mt-8 flex w-full items-center justify-center gap-2 bg-[#F26122] px-7 py-4 text-sm font-medium tracking-wide text-[#0B0F17] transition-all duration-300 hover:bg-[#FF7B3D] disabled:opacity-60"
                  >
                    {submitting ? "Submitting…" : "Start a Conversation"}
                    {!submitting && (
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
